import type { BodyMeasurementSource } from '@gramas/shared'
import { Button, NumberField, Sheet, TextArea } from '@gramas/ui'
import { useEffect, useState } from 'react'
import { saveBodyMeasurement } from '../../db/repo.js'
import { syncNow } from '../../sync/engine.js'

export interface WeightSheetProps {
  open: boolean
  date: string
  current: {
    weightKg: number | null
    bodyFatPct: number | null
    waistCm: number | null
    source?: BodyMeasurementSource
  } | null
  onOpenChange: (open: boolean) => void
}

/**
 * A weigh-in for one day.
 *
 * Body fat and waist are here because a scale that reports them makes them free
 * to record, and a weight that stalls while the waist shrinks is the case where
 * the number on its own would mislead.
 *
 * Still the way in when a smart scale is doing the weighing: it fills the same
 * row, and this is where its number gets corrected on the morning it decides a
 * person weighs 41 kg because they stood on the edge.
 */
export function WeightSheet({ open, date, current, onOpenChange }: WeightSheetProps) {
  const [weightKg, setWeightKg] = useState<number | null>(null)
  const [bodyFatPct, setBodyFatPct] = useState<number | null>(null)
  const [waistCm, setWaistCm] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setWeightKg(current?.weightKg ?? null)
    setBodyFatPct(current?.bodyFatPct ?? null)
    setWaistCm(current?.waistCm ?? null)
    setNote('')
  }, [open, current])

  const complete = weightKg != null || bodyFatPct != null || waistCm != null

  const save = async () => {
    if (!complete || saving) return
    setSaving(true)
    try {
      await saveBodyMeasurement({
        measuredOn: date,
        weightKg,
        bodyFatPct,
        waistCm,
        note: note.trim() || null,
      })
      void syncNow()
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Svoris"
      description={`Matavimas ${date} · pasveriama ryte, prieš valgį`}
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          disabled={!complete || saving}
          onClick={() => void save()}
        >
          {saving ? 'Saugoma…' : 'Išsaugoti'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        {current?.source === 'scale' && (
          <p className="text-xs text-mute">
            Šio ryto įrašą atsiuntė svarstyklės. Gali pataisyti ranka — tavo skaičius bus viršesnis.
          </p>
        )}
        <NumberField label="Svoris" suffix="kg" value={weightKg} onChange={setWeightKg} />
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label="Riebalų dalis"
            suffix="%"
            hint="Nebūtina"
            value={bodyFatPct}
            onChange={setBodyFatPct}
          />
          <NumberField
            label="Juosmuo"
            suffix="cm"
            hint="Nebūtina"
            value={waistCm}
            onChange={setWaistCm}
          />
        </div>
        <TextArea
          label="Pastaba"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="pvz. po kelionės"
        />
      </div>
    </Sheet>
  )
}
